import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule/Schedule';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';
import { ScheduleFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import {
  ScheduleAvailability,
  ScheduleAvailabilityAttributeValue,
  ScheduleAvailabilityKeys,
  ScheduleAvailabilityRelationshipKeys,
  ScheduleAvailabilitySerializedEntity,
  ScheduleAvailabilityState,
} from './types';

export const scheduleAvailabilitiesAdapter =
  createEntityAdapter<ScheduleAvailability>();

export const initialState: ScheduleAvailabilityState = {
  ...scheduleAvailabilitiesAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAll: scheduleAvailabilitiesAdapter.setAll,
    removeAll: scheduleAvailabilitiesAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (
        state,
        action: ScheduleFetchAction<ScheduleAvailabilitySerializedEntity>
      ) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const scheduleAvailabilities: ScheduleAvailability[] =
              action.payload.included
                .filter(
                  (entity): entity is ScheduleAvailabilitySerializedEntity =>
                    entity.type === 'scheduleAvailability'
                )
                .map(entity => ({
                  id: entity.id,
                  type: entity.type,
                  title: entity.attributes.title,
                  startAt: entity.attributes.startAt,
                  endAt: entity.attributes.endAt,
                  allDay: entity.attributes.allDay,
                  date: entity.attributes.date,
                  job: entity.relationships?.job?.data,
                }));
            scheduleAvailabilitiesAdapter.setAll(state, scheduleAvailabilities);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const {
  selectById: selectScheduleAvailabilityById,
  selectIds: selectScheduleAvailabilityIds,
  selectEntities: selectScheduleAvailabilityEntities,
  selectAll: selectAllScheduleAvailabilities,
  selectTotal: selectTotalScheduleAvailabilities,
} = scheduleAvailabilitiesAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export const { reducer, actions } = slice;

export const selectScheduleAvailabilitiesStatus = (state: RootState) =>
  getSliceState(state)?.[SLICE_NAME]?.status || 'idle';

export const selectScheduleAvailabilitiesFetchIsPending = (
  state: RootState
): boolean => selectScheduleAvailabilitiesStatus(state) === 'pending';

export const selectScheduleAvailabilityAttribute = <
  T extends keyof ScheduleAvailability
>({
  state,
  id,
  attribute,
}: {
  state: RootState;
  id: EntityId;
  attribute: T;
}) =>
  selectAttributeForEntity({
    state,
    attribute,
    entitySelector: selectScheduleAvailabilityById,
    id,
  });

export const selectScheduleAvailabilityRelationshipId = ({
  state,
  id,
  relationship,
}: {
  state: RootState;
  id: EntityId;
  relationship: ScheduleAvailabilityRelationshipKeys;
}): EntityId | undefined => {
  const scheduleAvailability = selectScheduleAvailabilityById(state, id);
  if (!scheduleAvailability) return undefined;

  if (relationship === 'job') {
    return scheduleAvailability.job?.id;
  }

  return undefined;
};

export const selectScheduleAvailabilitiesWhereAttribute = createCachedSelector(
  [
    selectAllScheduleAvailabilities,
    (
      _state: RootState,
      {
        attribute,
        value,
      }: {
        attribute: ScheduleAvailabilityKeys;
        value: ScheduleAvailabilityAttributeValue;
      }
    ) => ({
      attribute,
      value,
    }),
  ],
  (
    scheduleAvailabilities: ScheduleAvailability[],
    {
      attribute,
      value,
    }: {
      attribute: ScheduleAvailabilityKeys;
      value: ScheduleAvailabilityAttributeValue;
    }
  ) =>
    scheduleAvailabilities.filter(
      (scheduleAvailability: ScheduleAvailability) =>
        scheduleAvailability[attribute] === value
    )
)(
  (
    _,
    params?: {
      attribute: ScheduleAvailabilityKeys;
      value: ScheduleAvailabilityAttributeValue;
    }
  ) =>
    `selectScheduleAvailabilitiesWhereAttribute-${
      params?.attribute || 'unknown'
    }-${params?.value || 'unknown'}`
);

/**
 * example response for scheduleAvailabilities when fetching schedule:
 *
 *    {
            "id": "2025-06-15-4623724-2494388",
            "type": "scheduleAvailability",
            "attributes": {
                "title": "Unavailable",
                "allDay": false,
                "startAt": "2025-06-15 17:00",
                "endAt": "2025-06-15 22:00",
                "date": "2025-06-15"
            },
            "relationships": {
                "job": {
                    "data": {
                        "id": "2494388",
                        "type": "job"
                    }
                }
            }
        },
 */
